import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, DatePicker, Drawer, Spin, Table } from "antd";
import dayjs from "dayjs";
import HighchartsReact from "highcharts-react-official";
import { getUsageDeviceBrandUsers, getUsageDevices } from "../../../apis/analyticsUsageApi";
import { barChartOptions, donutOptions, formatNumber, formatPct, THEME } from "./chartHelpers";
import { buildUserColumns } from "./WidgetDetailBreakdown";
import { analyticsDrawerOverlayProps } from "./analyticsModalUtils";
import { ANALYTICS_DATE_PICKER_CLASS } from "./analyticsTheme";
import AnalyticsHelpIcon from "./AnalyticsHelpIcon";
import AnalyticsPanelHead from "./AnalyticsPanelHead";

const { RangePicker } = DatePicker;
const MAX_RANGE_DAYS = 90;
const defaultRange = () => [dayjs().subtract(29, "day").startOf("day"), dayjs().endOf("day")];

const DEVICE_ICONS = {
  Apple: "fa-brands fa-apple",
  Samsung: "fa-solid fa-mobile-screen",
  Google: "fa-brands fa-google",
  Xiaomi: "fa-solid fa-mobile",
};

const AnalyticsDevices = () => {
  const [dateRange, setDateRange] = useState(defaultRange);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [brandUsers, setBrandUsers] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const params = useMemo(
    () => ({
      from: dateRange[0].format("YYYY-MM-DD"),
      to: dateRange[1].format("YYYY-MM-DD"),
    }),
    [dateRange]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getUsageDevices(params);
      setData(res?.result || null);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to load device data");
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const openBrand = async (brand) => {
    setSelectedBrand(brand);
    setDrawerLoading(true);
    setBrandUsers([]);
    try {
      const res = await getUsageDeviceBrandUsers({ ...params, brand });
      setBrandUsers(res?.result?.users || []);
    } finally {
      setDrawerLoading(false);
    }
  };

  const brandDonut = useMemo(
    () => donutOptions((data?.brands || []).map((b) => ({ label: b.label, users: b.users })), "Devices"),
    [data]
  );

  const brandBar = useMemo(() => {
    const items = data?.brands || [];
    return barChartOptions({
      categories: items.map((b) => b.label),
      data: items.map((b) => b.users),
      color: THEME.primary,
    });
  }, [data]);

  return (
    <div className="analytics-dashboard analytics-devices">
      <header className="analytics-dashboard__header">
        <div>
          <h1>
            Devices
            <AnalyticsHelpIcon helpKey="devices-page" />
          </h1>
          <p>Connected users by device brand (Samsung, Apple, etc.) from mobile event properties</p>
        </div>
        <div className="analytics-dashboard__controls">
          <RangePicker
            popupClassName={ANALYTICS_DATE_PICKER_CLASS}
            value={dateRange}
            onChange={(dates) => {
              if (!dates?.[0] || !dates?.[1]) return;
              if (dates[1].diff(dates[0], "day") > MAX_RANGE_DAYS) {
                setError(`Max range is ${MAX_RANGE_DAYS} days`);
                return;
              }
              setDateRange(dates);
            }}
            allowClear={false}
            disabledDate={(c) => c && c > dayjs().endOf("day")}
          />
          <Button type="primary" onClick={load} loading={loading}>
            Refresh
          </Button>
        </div>
      </header>

      {error && <Alert type="error" message={error} closable onClose={() => setError(null)} showIcon />}

      {loading && !data ? (
        <div className="analytics-dashboard__loading">
          <Spin size="large" tip="Loading devices..." />
        </div>
      ) : (
        <>
          <div className="analytics-kpi-grid analytics-kpi-grid--user">
            <div className="analytics-kpi-card">
              <span className="analytics-kpi-card__label">Unique users</span>
              <div className="analytics-kpi-card__value">{formatNumber(data?.totals?.unique_users)}</div>
            </div>
            <div className="analytics-kpi-card">
              <span className="analytics-kpi-card__label">Device brands</span>
              <div className="analytics-kpi-card__value">{formatNumber(data?.totals?.brands)}</div>
            </div>
          </div>

          <div className="analytics-grid analytics-grid--2">
            <div className="analytics-panel">
              <AnalyticsPanelHead title="Brand distribution" helpKey="devices-distribution" />
              <HighchartsReact options={brandDonut} />
            </div>
            <div className="analytics-panel">
              <AnalyticsPanelHead title="Users by brand" helpKey="devices-by-brand" />
              <HighchartsReact options={brandBar} />
            </div>
          </div>

          <AnalyticsPanelHead title="Browse by brand" helpKey="devices-cards" className="analytics-panel__head--section" />
          <div className="analytics-device-grid">
            {(data?.brands || []).map((brand) => (
              <button
                key={brand.brand}
                type="button"
                className="analytics-device-card"
                onClick={() => openBrand(brand.brand)}
              >
                <i className={DEVICE_ICONS[brand.brand] || "fa-solid fa-mobile-screen-button"} />
                <strong>{brand.label}</strong>
                <span>{formatNumber(brand.users)} users</span>
                <small>{formatPct(brand.pct || 0)}</small>
                {brand.models?.length > 0 && (
                  <div className="analytics-device-card__models">
                    Top: {brand.models[0].model}
                  </div>
                )}
                <span className="analytics-device-card__cta">
                  View users <i className="fa-solid fa-arrow-right" />
                </span>
              </button>
            ))}
            {!data?.brands?.length && (
              <p className="analytics-empty">No device data in this period</p>
            )}
          </div>
        </>
      )}

      <Drawer
        title={selectedBrand ? `${selectedBrand} users` : "Users"}
        open={Boolean(selectedBrand)}
        onClose={() => setSelectedBrand(null)}
        width={720}
        className="analytics-detail-drawer"
        {...analyticsDrawerOverlayProps}
      >
        {drawerLoading ? (
          <Spin tip="Loading users..." />
        ) : (
          <Table
            size="small"
            className="analytics-table"
            columns={buildUserColumns()}
            dataSource={brandUsers.map((r, i) => ({ ...r, key: r.distinct_id || i }))}
            pagination={{ pageSize: 15, size: "small" }}
          />
        )}
      </Drawer>
    </div>
  );
};

export default AnalyticsDevices;
